import types from '../../mutation-types';
import getters, { getSelectedChatConversation } from './getters';
import actions from './actions';
import { findPendingMessageIndex } from './helpers';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { CONTENT_TYPES } from 'dashboard/components-next/message/constants.js';

const state = {
  allConversations: [],
  attachments: {},
  listLoadingStatus: true,
  // Mensagem de erro da última tentativa de carregar a lista. Existe porque
  // falha de rede/API precisa virar tela ("tentar de novo"), não spinner
  // infinito — ver CLEAR_LIST_LOADING_STATUS no `finally` das actions.
  listErrorStatus: null,
  chatStatusFilter: wootConstants.STATUS_TYPE.OPEN,
  chatSortFilter: wootConstants.SORT_BY_TYPE.LATEST,
  currentInbox: null,
  selectedChatId: null,
  appliedFilters: [],
  contextMenuChatId: null,
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  conversationFilters: {},
  copilotAssistant: {},
};

const getConversationById = _state => conversationId => {
  return _state.allConversations.find(c => c.id === conversationId);
};

// mutations
export const mutations = {
  [types.SET_ALL_CONVERSATION](_state, conversationList) {
    const newAllConversations = [..._state.allConversations];
    conversationList.forEach(conversation => {
      const indexInCurrentList = newAllConversations.findIndex(
        c => c.id === conversation.id
      );
      if (indexInCurrentList < 0) {
        newAllConversations.push(conversation);
      } else {
        const existingConversation = newAllConversations[indexInCurrentList];

        // Mesma trava de "ignora evento fora de ordem" que UPDATE_CONVERSATION
        // já usa (index.js, mutation separada, para o evento de websocket
        // conversation_updated). SET_ALL_CONVERSATION nunca teve isso —
        // um refresh de lista em voo mais lento que uma mudança recente
        // (ex: transferir a conversa pra si mesmo) podia chegar DEPOIS e
        // sobrescrever campos como meta.assignee de volta pro valor antigo,
        // já que esse mutation troca `meta` inteiro (spread de `conversation`).
        if (
          conversation.updated_at &&
          existingConversation.updated_at &&
          conversation.updated_at < existingConversation.updated_at
        ) {
          return;
        }

        // Replace o resto dos dados (status, labels, etc — pra resolver
        // conversa não atualizar ao reconectar o websocket), mas SEMPRE
        // preserva messages/dataFetched/allMessagesLoaded já cacheados,
        // não só quando é a conversa selecionada. Antes, qualquer refresh
        // de lista em background (websocket reconectando, poll) que pegasse
        // uma conversa NÃO selecionada apagava essas 3 chaves por completo
        // (o payload de lista não inclui messages) — ao reabrir essa
        // conversa depois, `data.messages[0]` virava undefined e o fetch
        // inicial quebrava silenciosamente, deixando o spinner de
        // carregamento girando pra sempre (dataFetched nunca setava).
        newAllConversations[indexInCurrentList] = {
          ...conversation,
          allMessagesLoaded: existingConversation.allMessagesLoaded,
          messages: existingConversation.messages,
          dataFetched: existingConversation.dataFetched,
        };
      }
    });
    _state.allConversations = newAllConversations;
  },
  [types.EMPTY_ALL_CONVERSATION](_state) {
    _state.allConversations = [];
    _state.selectedChatId = null;
  },
  [types.CLEAR_CONVERSATION_LIST](_state) {
    // CAUSA RAIZ do spinner de "carregando mensagens antigas" ficar órfão:
    // toda troca de aba (Minhas/Fila/Todos) busca página 1, que roda ESSE
    // mutation ANTES do SET_ALL_CONVERSATION — zerando o array inteiro fazia
    // o merge/preserve de messages/dataFetched/allMessagesLoaded (lá embaixo)
    // nunca rodar de verdade pra NENHUMA conversa, incluindo a que tava
    // aberta: ela reentrava como "nova" sem histórico, e o fetch inicial
    // (setActiveChat) quebrava silenciosamente (`data.messages[0]` undefined)
    // deixando o spinner girando pra sempre. Preserva a conversa selecionada.
    const selected = _state.allConversations.find(
      c => c.id === _state.selectedChatId
    );
    _state.allConversations = selected ? [selected] : [];
  },
  [types.SET_ALL_MESSAGES_LOADED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.allMessagesLoaded = true;
    }
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.allMessagesLoaded = false;
    }
  },
  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.selectedChatId = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (!data.length) return;

    const chat = getConversationById(_state)(id);
    // A conversa pode ter saído da lista enquanto o fetch de histórico estava
    // em voo (troca de aba/filtro roda CLEAR_CONVERSATION_LIST).
    if (!chat?.messages) return;

    // Nunca a mesma mensagem duas vezes na tela (relato do Dr. Henrique,
    // 06/10/2026: um modelo enviado aparecia em dobro, no banco só uma vez).
    // Se uma busca de histórico em voo trouxer algo que o websocket já
    // colocou, fica a cópia que já está.
    const novas = data.filter(m => !chat.messages.some(i => i.id === m.id));
    chat.messages.unshift(...novas);
  },
  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    _state.attachments[id] = [...data];
  },
  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (!chat) return;
    chat.messages = data;
  },

  [types.SET_CHAT_DATA_FETCHED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.dataFetched = true;
    }
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, activeChat) {
    if (activeChat) {
      _state.selectedChatId = activeChat.id;
    }
  },

  [types.ASSIGN_AGENT](_state, { conversationId, assignee }) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.meta.assignee = assignee;
    }
  },

  [types.ASSIGN_TEAM](_state, { team, conversationId }) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    chat.meta.team = team;
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.last_activity_at = lastActivityAt;
    }
  },
  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    chat.priority = priority;
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](
    _state,
    { conversationId, customAttributes }
  ) {
    const conversation = _state.allConversations.find(
      c => c.id === conversationId
    );
    if (conversation) {
      conversation.custom_attributes = {
        ...conversation.custom_attributes,
        ...customAttributes,
      };
    }
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    const conversation =
      getters.getConversationById(_state)(conversationId) || {};
    conversation.snoozed_until = snoozedUntil;
    conversation.status = status;
  },

  // `conversationId` é opcional e cai na conversa selecionada quando ausente
  // (assinatura antiga). Silenciar pelo menu de contexto da LISTA mira uma
  // conversa que não é a aberta — sem o id, mutava a errada, e sem conversa
  // aberta quebrava com TypeError.
  [types.MUTE_CONVERSATION](_state, conversationId) {
    const chat = conversationId
      ? getConversationById(_state)(conversationId)
      : getSelectedChatConversation(_state)[0];
    if (!chat) return;

    chat.muted = true;
  },

  [types.UNMUTE_CONVERSATION](_state, conversationId) {
    const chat = conversationId
      ? getConversationById(_state)(conversationId)
      : getSelectedChatConversation(_state)[0];
    if (!chat) return;

    chat.muted = false;
  },

  // advup/Lumos — indicador "IA ativa" + toggle manual por conversa (§5.2.1). Endpoint é
  // um toggle único (não pause/resume separados), então a mutation espelha isso: inverte
  // a partir do estado atual. O próximo push_data real (broadcast automático de
  // conversation.updated) corrige qualquer divergência.
  [types.LIBERAR_CONGELAMENTO](_state, contactId) {
    _state.allConversations
      .filter(chat => chat.meta?.sender?.id === contactId)
      .forEach(chat => {
        chat.contato_congelado = null;
      });
  },

  [types.TOGGLE_AI_PAUSE](_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return;

    if (chat.ai_paused_at) {
      chat.ai_paused_at = null;
      chat.ai_paused_by = null;
      chat.ai_active = true;
    } else {
      chat.ai_paused_at = new Date().toISOString();
      chat.ai_active = false;
    }
  },

  [types.ADD_CONVERSATION_ATTACHMENTS](_state, message) {
    // early return if the message has not been sent, or has no attachments
    if (
      message.status !== MESSAGE_STATUS.SENT ||
      !message.attachments?.length
    ) {
      return;
    }

    const id = message.conversation_id;
    const existingAttachments = _state.attachments[id] || [];

    const attachmentsToAdd = message.attachments.filter(attachment => {
      // if the attachment is not already in the store, add it
      // this is to prevent duplicates
      return !existingAttachments.some(
        existingAttachment => existingAttachment.id === attachment.id
      );
    });

    // replace the attachments in the store
    _state.attachments[id] = [...existingAttachments, ...attachmentsToAdd];
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS](_state, message) {
    if (message.status !== MESSAGE_STATUS.SENT) return;

    const { conversation_id: id } = message;
    const existingAttachments = _state.attachments[id] || [];
    if (!existingAttachments.length) return;

    _state.attachments[id] = existingAttachments.filter(attachment => {
      return attachment.message_id !== message.id;
    });
  },

  // Não emite evento de scroll: a rolagem é decisão da VIEW, não do store.
  // `MessagesView` já observa `messagesCount` e rola sozinha quando a
  // contagem muda — o emit daqui era um segundo caminho pro mesmo efeito,
  // e o único que ignorava `hasUserScrolled`.
  [types.ADD_MESSAGE]({ allConversations }, message) {
    const { conversation_id: conversationId } = message;
    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });
    if (!chat) return;

    const pendingMessageIndex = findPendingMessageIndex(chat, message);
    if (pendingMessageIndex !== -1) {
      chat.messages[pendingMessageIndex] = message;
    } else {
      chat.messages.push(message);
      chat.timestamp = message.created_at;
      const { conversation: { unread_count: unreadCount = 0 } = {} } = message;
      chat.unread_count = unreadCount;
    }
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    // Guard: Skip conversations without valid id (display_id)
    // This can happen when WebSocket message arrives before display_id is set
    if (!conversation?.id) {
      return;
    }
    const exists = _state.allConversations.some(c => c.id === conversation.id);
    if (!exists) {
      _state.allConversations.push(conversation);
    }
  },

  // Achado real (Victor, 2026-07-23): mudar etiqueta pela UI só escrevia em
  // conversationLabels.records[id] (módulo separado) — o card da lista e o
  // filtro por etiqueta leem `conversation.labels` de allConversations, que
  // só sincronizava quando o evento `conversation.updated` chegasse via
  // ActionCable. Sem esse evento (atraso/perda), o chip ficava desatualizado
  // até F5. Mutation dedicada pra aplicar otimisticamente, mesma classe de
  // fix que toggleStatus/markRead já faziam pra status/unread.
  [types.UPDATE_CONVERSATION_LABELS](_state, { id, labels }) {
    const { allConversations } = _state;
    const index = allConversations.findIndex(c => c.id === Number(id));
    if (index > -1) {
      allConversations[index] = { ...allConversations[index], labels };
    }
  },

  [types.DELETE_CONVERSATION](_state, conversationId) {
    _state.allConversations = _state.allConversations.filter(
      c => c.id !== conversationId
    );
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    const { allConversations } = _state;
    const index = allConversations.findIndex(c => c.id === conversation.id);

    if (index > -1) {
      const selectedConversation = allConversations[index];

      // ignore out of order events
      if (conversation.updated_at < selectedConversation.updated_at) {
        return;
      }

      // Achado real (bug de fila reportado pelo Eduardo — conversa recente
      // "voltando" pro fim da lista de mais recentes): este evento cobre
      // vários gatilhos (assignee.changed, conversation.updated etc), cada
      // um carregando seu PRÓPRIO snapshot de last_activity_at tirado em
      // momentos diferentes. A trava acima só compara updated_at — um
      // evento mais novo em updated_at ainda pode carregar um
      // last_activity_at mais VELHO que o que message.created (via
      // UPDATE_CONVERSATION_LAST_ACTIVITY) já tinha aplicado, regredindo a
      // ordenação por "mais recentes". last_activity_at só deve mudar pela
      // mutation dedicada, nunca por esse spread genérico.
      const {
        messages,
        last_activity_at: _ignoredLastActivityAt,
        ...updates
      } = conversation;
      allConversations[index] = { ...selectedConversation, ...updates };
      // Aqui NÃO se rola a conversa. Este evento cobre qualquer alteração de
      // metadado (etiqueta, responsável, prioridade, status) e não traz
      // mensagem nova — `messages` é justamente o que o destructuring acima
      // descarta. O emit que existia aqui chamava `scrollToBottom()` sem
      // olhar `hasUserScrolled`: quem estivesse lendo o histórico era jogado
      // pro fim porque um colega mudou uma etiqueta noutro lugar.
    } else {
      const { conversationType } = _state.conversationFilters || {};
      const { MENTION, PARTICIPATING } = wootConstants.CONVERSATION_TYPE;
      const hasAppliedFilters = (_state.appliedFilters || []).length > 0;
      // Don't add conversations not in the current filter set; otherwise
      // WebSocket events for non-matching conversations leak back into
      // allConversations while a filter is active.
      if (
        !hasAppliedFilters &&
        ![MENTION, PARTICIPATING].includes(conversationType)
      ) {
        _state.allConversations.push(conversation);
      }
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.SET_LIST_ERROR_STATUS](_state, error) {
    _state.listErrorStatus = error ? String(error) : 'error';
  },

  [types.CLEAR_LIST_ERROR_STATUS](_state) {
    _state.listErrorStatus = null;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (chat) {
      chat.agent_last_seen_at = lastSeen;
      chat.unread_count = unreadCount;
    }
  },
  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  // Update assignee on action cable message
  [types.UPDATE_ASSIGNEE](_state, payload) {
    const chat = getConversationById(_state)(payload.id);
    if (chat) {
      chat.meta.assignee = payload.assignee;
    }
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.meta.sender = payload;
    }
  },

  [types.UPDATE_CONVERSATION_CALL_STATUS](
    _state,
    { conversationId, callStatus }
  ) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    chat.additional_attributes = {
      ...chat.additional_attributes,
      call_status: callStatus,
    };
  },

  [types.UPDATE_MESSAGE_CALL_STATUS](_state, { conversationId, callStatus }) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    const lastCall = (chat.messages || []).findLast(
      m => m.content_type === CONTENT_TYPES.VOICE_CALL
    );

    if (!lastCall) return;

    lastCall.content_attributes ??= {};
    lastCall.content_attributes.data = {
      ...lastCall.content_attributes.data,
      status: callStatus,
    };
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.can_reply = canReply;
    }
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    const chats = _state.allConversations.filter(
      c => c.meta.sender.id !== contactId
    );
    _state.allConversations = chats;
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { conversationId, messageId }
  ) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },

  [types.SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },
  [types.UPDATE_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },
  [types.SET_INBOX_CAPTAIN_ASSISTANT](_state, data) {
    _state.copilotAssistant = data.assistant;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
